/*!
 * RUN IT LONDON — blackjack core.
 *
 * Pure game + reward logic. No DOM, no theme globals, no network, so it can be
 * unit tested under Node and reused by every surface (popup, standalone page).
 *
 * Reward percentages live in DEFAULT_REWARDS / PERCENT_BY_TIER and are the
 * *display* defaults only. The browser is never authoritative: the claim
 * request sends the result key and the backend derives the percentage from the
 * same map (see rewards-worker/src/tiers.js — keep the two in sync).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.RunItBlackjackCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var SUITS = ['spades', 'hearts', 'diamonds', 'clubs'];
  var RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

  var SUIT_LABEL = {
    spades: 'spades',
    hearts: 'hearts',
    diamonds: 'diamonds',
    clubs: 'clubs'
  };

  var RANK_LABEL = {
    A: 'Ace',
    J: 'Jack',
    Q: 'Queen',
    K: 'King'
  };

  var RED_SUITS = { hearts: true, diamonds: true };

  /* ------------------------------------------------------------------ *
   * Rewards — the single place percentages are defined for the frontend.
   * ------------------------------------------------------------------ */
  var DEFAULT_REWARDS = { lose: 10, push: 10, win: 12, blackjack: 20 };

  /* Detailed result key -> reward tier. One table, used by both sides. */
  var TIER_BY_RESULT = {
    lose: 'lose',
    bust: 'lose',
    dealer_win: 'lose',
    dealer_blackjack: 'lose',
    push: 'push',
    both_blackjack: 'push',
    win: 'win',
    dealer_bust: 'win',
    blackjack: 'blackjack'
  };

  /* Percentages the backend is allowed to return. Anything else is discarded. */
  /* Minimum order subtotal (GBP) by percentage — mirror of the worker's
     MIN_SUBTOTAL_BY_PERCENT (rewards-worker/src/tiers.js). */
  var MIN_SUBTOTAL_BY_PERCENT = { 20: 150 };

  /* 15 is the rate a win paid until Sep 2026; those codes are still shown. */
  var ALLOWED_PERCENTS = [10, 12, 15, 20];

  /* Dealer hits below this total and stands on it. Stand on all 17. */
  var DEALER_STANDS_ON = 17;

  /*
   * Codes carry the tier's percentage and a random suffix — `RUNIT15-K7X4Q9` —
   * from an unambiguous alphabet (no O/0, I/1). The prefix is a courtesy to the
   * shopper, not an authorisation: the percentage the discount actually gives is
   * decided server-side and never read back off the string.
   *
   * `RIL-XXXXXX` is the original shape and stays valid. Every code minted under
   * it is a real Shopify discount, and refusing to recognise one would strand it.
   * Keep in step with rewards-worker/src/tiers.js.
   */
  var CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  var CODE_SUFFIX = '[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{6}';
  var CODE_PATTERN = new RegExp('^(?:RUNIT(?:10|12|15|20)-' + CODE_SUFFIX + '|RIL-' + CODE_SUFFIX + ')$');

  /* ------------------------------------------------------------------ *
   * Randomness
   * ------------------------------------------------------------------ */

  function cryptoSource() {
    if (typeof globalThis !== 'undefined' && globalThis.crypto && typeof globalThis.crypto.getRandomValues === 'function') {
      return globalThis.crypto;
    }
    return null;
  }

  /**
   * Uniform integer in [0, max) using crypto when available, with rejection
   * sampling so the distribution is not biased by the modulo.
   */
  function randomInt(max) {
    if (!(max > 0)) return 0;
    var source = cryptoSource();
    if (source) {
      var limit = Math.floor(4294967296 / max) * max;
      var buffer = new Uint32Array(1);
      var value;
      var guard = 0;
      do {
        source.getRandomValues(buffer);
        value = buffer[0];
        guard++;
      } while (value >= limit && guard < 64);
      return value % max;
    }
    return Math.floor(Math.random() * max);
  }

  function hasSecureRandom() {
    return cryptoSource() !== null;
  }

  /* ------------------------------------------------------------------ *
   * Deck
   * ------------------------------------------------------------------ */

  /** A fresh, ordered 52-card deck — every card exactly once. */
  function createDeck() {
    var deck = [];
    for (var s = 0; s < SUITS.length; s++) {
      for (var r = 0; r < RANKS.length; r++) {
        deck.push({ rank: RANKS[r], suit: SUITS[s] });
      }
    }
    return deck;
  }

  /** Fisher–Yates, in place, using the crypto-backed randomInt. */
  function shuffle(deck) {
    for (var i = deck.length - 1; i > 0; i--) {
      var j = randomInt(i + 1);
      var tmp = deck[i];
      deck[i] = deck[j];
      deck[j] = tmp;
    }
    return deck;
  }

  /** A shuffled 52-card deck with draw support. No card is ever repeated. */
  function createShoe() {
    var cards = shuffle(createDeck());
    return {
      remaining: function () {
        return cards.length;
      },
      draw: function () {
        if (!cards.length) cards = shuffle(createDeck());
        return cards.pop();
      }
    };
  }

  /* ------------------------------------------------------------------ *
   * Scoring
   * ------------------------------------------------------------------ */

  function cardValue(card) {
    if (!card) return 0;
    if (card.rank === 'A') return 11;
    if (card.rank === 'J' || card.rank === 'Q' || card.rank === 'K') return 10;
    return parseInt(card.rank, 10) || 0;
  }

  /**
   * Best valid total. Aces start at 11 and drop to 1, one at a time, until the
   * hand fits — which is the correct "count enough aces as 1" behaviour for any
   * number of aces (e.g. A+A+9 = 21).
   */
  function handTotal(cards) {
    var list = cards || [];
    var total = 0;
    var aces = 0;
    for (var i = 0; i < list.length; i++) {
      if (list[i].rank === 'A') aces++;
      total += cardValue(list[i]);
    }
    while (total > 21 && aces > 0) {
      total -= 10;
      aces--;
    }
    return {
      total: total,
      soft: aces > 0 && total <= 21,
      bust: total > 21,
      aces: aces
    };
  }

  function isBlackjack(cards) {
    var list = cards || [];
    return list.length === 2 && handTotal(list).total === 21;
  }

  function isBust(cards) {
    return handTotal(cards).bust;
  }

  function dealerShouldHit(total) {
    return total < DEALER_STANDS_ON;
  }

  /** Total over the face-up cards only — what the player is allowed to see. */
  function visibleTotal(cards) {
    var visible = [];
    var list = cards || [];
    for (var i = 0; i < list.length; i++) {
      if (!list[i].hidden) visible.push(list[i]);
    }
    return handTotal(visible);
  }

  /* ------------------------------------------------------------------ *
   * Results
   * ------------------------------------------------------------------ */

  /**
   * Resolve a completed hand. Dealer blackjack is only compared when the
   * dealer actually holds two cards totalling 21.
   */
  function resolveOutcome(playerCards, dealerCards) {
    var player = handTotal(playerCards);
    var dealer = handTotal(dealerCards);
    var playerBJ = isBlackjack(playerCards);
    var dealerBJ = isBlackjack(dealerCards);

    if (playerBJ && dealerBJ) return 'both_blackjack';
    if (playerBJ) return 'blackjack';
    if (dealerBJ) return 'dealer_blackjack';
    if (player.bust) return 'bust';
    if (dealer.bust) return 'dealer_bust';
    if (player.total > dealer.total) return 'win';
    if (dealer.total > player.total) return 'lose';
    return 'push';
  }

  function tierForResult(result) {
    return TIER_BY_RESULT[result] || 'lose';
  }

  /*
   * What the hand gets called.
   *
   * Keyed by REWARD TIER, with one exception: a bust is its own thing, because
   * "you went over" and "the dealer had you" are different hands and a shopper
   * who is told the wrong one notices immediately. Several lines per pool so a
   * shopper playing again does not read the same sentence twice, and every line
   * is outcome-honest — nothing here congratulates a lost hand.
   *
   * Copy, not state: the percentage a hand is worth is decided server-side and
   * never derived from these strings.
   */
  var RESULT_LINES = {
    win: [
      'You beat the house.',
      'House pays out.',
      "That'll do it.",
      'Dealer got smoked.'
    ],
    lose: [
      'So close.',
      'Dealer takes this one.',
      'Unlucky — run it back.',
      'House wins this round.'
    ],
    bust: [
      'Bust. Too much throttle.',
      'Over 21. House takes it.',
      'Sent it a little too hard.',
      'One card too many.'
    ],
    push: [
      'Push. Even hand.',
      'Dead even.',
      'Nobody takes it.',
      'Square — your money stays.'
    ],
    blackjack: [
      'BLACKJACK.',
      '21. Clean.',
      'Perfect hand.',
      'Natural. Nothing beats it.'
    ]
  };

  /**
   * A line for the hand that just finished.
   *
   * `outcome` is the detailed result — a bust picks from its own pool, every
   * other result from its tier's. `previous` is the line that was on screen a
   * moment ago and is never picked again, so two hands in a row cannot read the
   * same however the draw falls. `preferred` (the merchant's setting for this
   * tier) joins the pool instead of replacing it: their copy is still read, and
   * the line still varies. `pick` is injected by tests; it defaults to the
   * crypto-backed randomInt so the choice is not predictable from the hand.
   */
  function resultLine(outcome, previous, preferred, pick) {
    var key = outcome === 'bust' ? 'bust' : tierForResult(outcome);
    var pool = (RESULT_LINES[key] || []).slice();
    if (preferred && pool.indexOf(preferred) === -1) pool.unshift(preferred);
    if (!pool.length) return '';
    var choices = previous ? pool.filter(function (line) { return line !== previous; }) : pool;
    if (!choices.length) choices = pool;
    var index = pick ? pick(choices.length) : randomInt(choices.length);
    if (!(index >= 0) || index >= choices.length) index = 0;
    return choices[index];
  }

  /** Percentage the frontend *expects* to show — never sent to the server. */
  function percentForResult(result, rewards) {
    var map = rewards || DEFAULT_REWARDS;
    var tier = tierForResult(result);
    return typeof map[tier] === 'number' ? map[tier] : DEFAULT_REWARDS[tier];
  }

  function percentForTier(tier, rewards) {
    var map = rewards || DEFAULT_REWARDS;
    return typeof map[tier] === 'number' ? map[tier] : DEFAULT_REWARDS[tier];
  }

  /** Trust nothing from the network: shape, alphabet and allow-list. */
  function isValidCode(code) {
    return typeof code === 'string' && CODE_PATTERN.test(code);
  }

  function isAllowedPercent(percent) {
    return ALLOWED_PERCENTS.indexOf(percent) !== -1;
  }

  /** A code in the current shape, for the editor's own preview and for tests. */
  function buildCode(percent) {
    var tierPercent = ALLOWED_PERCENTS.indexOf(percent) !== -1 ? percent : 12;
    var out = 'RUNIT' + tierPercent + '-';
    for (var i = 0; i < 6; i++) out += CODE_ALPHABET[randomInt(CODE_ALPHABET.length)];
    return out;
  }

  function accessibleCardLabel(card) {
    if (!card) return '';
    var rank = RANK_LABEL[card.rank] || card.rank;
    return rank + ' of ' + (SUIT_LABEL[card.suit] || card.suit);
  }

  function isRedSuit(suit) {
    return RED_SUITS[suit] === true;
  }

  /* ------------------------------------------------------------------ *
   * Persistence / reward-farming guards
   * ------------------------------------------------------------------ */

  var STORAGE_KEY = 'runit_blackjack_v1';

  var EMPTY_STATE = {
    version: 1,
    seen: false,
    views: 0,
    dismissals: 0,
    plays: 0,
    lastDismissedAt: 0,
    firstSeenAt: 0,
    claimed: null,
    sms: null,
    /* The worker's id for the hand on the table. The cards, the totals and the
       reward all belong to this session, so it is the one value that has to
       survive a reload — everything else here is UI cache. An empty string
       means "no hand". */
    sessionId: '',
    /* The resolved outcome of the hand this shopper played, written the moment a
       hand finishes. It is what makes the reward FINAL: one shopper, one result,
       so a second deal is refused rather than re-rolled. `hand` beside it is the
       durable copy of the table it was won on, for a session that has no
       sessionStorage (a new tab, a cleared session). */
    result: null,
    hand: null
  };

  function emptyState() {
    var copy = {};
    for (var k in EMPTY_STATE) {
      if (Object.prototype.hasOwnProperty.call(EMPTY_STATE, k)) copy[k] = EMPTY_STATE[k];
    }
    return copy;
  }

  function safeStorage(kind) {
    try {
      var store = typeof window !== 'undefined' ? window[kind] : null;
      if (!store) return null;
      var probe = '__ril_bj_probe__';
      store.setItem(probe, '1');
      store.removeItem(probe);
      return store;
    } catch (e) {
      return null; /* private mode / blocked cookies — degrade quietly */
    }
  }

  /**
   * `undefined` means "use localStorage"; an explicit null means "no storage"
   * (theme-editor testing), so nothing is ever read or persisted.
   */
  function resolveStore(storage) {
    return storage === undefined ? safeStorage('localStorage') : storage;
  }

  function readState(storage) {
    var store = resolveStore(storage);
    if (!store) return emptyState();
    try {
      var raw = store.getItem(STORAGE_KEY);
      if (!raw) return emptyState();
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return emptyState();
      var state = emptyState();
      for (var k in state) {
        if (Object.prototype.hasOwnProperty.call(parsed, k) && typeof parsed[k] === typeof state[k]) {
          state[k] = parsed[k];
        }
      }
      if (parsed.claimed && typeof parsed.claimed === 'object') state.claimed = parsed.claimed;
      if (parsed.sms && typeof parsed.sms === 'object') state.sms = parsed.sms;
      if (parsed.result && typeof parsed.result === 'object') state.result = parsed.result;
      if (parsed.hand && typeof parsed.hand === 'object') state.hand = parsed.hand;
      return state;
    } catch (e) {
      return emptyState();
    }
  }

  function writeState(storage, patch) {
    var store = resolveStore(storage);
    var next = readState(store);
    if (patch && typeof patch === 'object') {
      for (var k in patch) {
        if (Object.prototype.hasOwnProperty.call(patch, k)) next[k] = patch[k];
      }
    }
    if (store) {
      try {
        store.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (e) {
        /* quota — the popup still works, it just will not remember */
      }
    }
    return next;
  }

  function clearState(storage) {
    var store = resolveStore(storage);
    if (store) {
      try {
        store.removeItem(STORAGE_KEY);
      } catch (e) {
        /* noop */
      }
    }
  }

  /**
   * Should we auto-open? A claimed reward, an active cooldown or an in-flight
   * session all suppress the automatic open. Reopening is always a manual act.
   */
  function shouldAutoOpen(state, options) {
    var opts = options || {};
    var now = typeof opts.now === 'number' ? opts.now : Date.now();
    var cooldownDays = typeof opts.cooldownDays === 'number' ? opts.cooldownDays : 0;

    if (opts.enabled === false) return false;
    if (!state) return true;
    if (state.claimed) return false;
    if (opts.testMode) return true;
    if (cooldownDays <= 0) return !state.seen ? true : false;
    if (!state.lastDismissedAt) return !state.seen ? true : false;
    var elapsed = now - state.lastDismissedAt;
    return elapsed >= cooldownDays * 86400000;
  }

  /** A claimed reward is issued once: the first claim is the only claim. */
  function canClaim(state) {
    return !(state && state.claimed && state.claimed.code);
  }

  /**
   * May this shopper play again for fun?
   *
   * Only once their reward EXISTS. The first hand decides the reward — the
   * backend prices a hand once and always resolves a later claim to the same
   * reward — so offering a replay before the claim would be a way to deal hand
   * after hand and only claim the good one. A claimed code is the mark of a
   * reward that is already theirs, and it is the only thing that opens this door.
   */
  function canReplay(state) {
    return Boolean(state && state.claimed && state.claimed.code);
  }

  /**
   * The session a CLAIM must be priced from: the hand that EARNED the reward.
   *
   * A shopper can play for fun after their reward is decided, and every one of
   * those hands is a session the worker dealt. None of them may become the hand
   * a discount is priced from, or the reward would be re-decided by whichever
   * hand the shopper happened to be looking at when they finally typed an
   * address. `result.sessionId` is written with the result by the hand that
   * earned it (see `saveResult`'s caller in runit-blackjack.js) and is therefore
   * the only session a claim is allowed to name; the live session is the
   * fallback for the path where no hand has resolved yet.
   *
   * Storage is not a security boundary and is not being used as one here: the
   * worker prices whatever session this names and refuses a replay session, so
   * a hand-edited value can only ever ask for a reward the server has already
   * decided.
   */
  function claimSession(state) {
    var result = state && state.result;
    var earned = result && typeof result === 'object' ? result.sessionId : '';
    if (typeof earned === 'string' && earned) return earned;
    var live = state && state.sessionId;
    return typeof live === 'string' && live ? live : '';
  }

  /**
   * The reward a replay shows: the one the shopper already holds.
   *
   * `issued` is what the backend answered for a replay claim (or null when it
   * could not be reached), `cached` is the claim in storage, and `rewards` is the
   * display table. The order matters and is the whole point — the server's answer
   * wins, storage is the fallback, and the hand just played is never consulted,
   * because a replay can neither raise nor lower a reward.
   *
   * Returns null when neither source names a usable reward, so the caller can
   * decide what to show rather than being handed half a reward.
   */
  function replayReward(issued, cached, rewards) {
    var source = issued && issued.code ? issued : cached;
    if (!source || !source.code) return null;
    var percent = isAllowedPercent(source.percent)
      ? source.percent
      : percentForTier(source.tier, rewards);
    if (typeof percent !== 'number') return null;
    return {
      code: source.code,
      percent: percent,
      tier: source.tier || null
    };
  }

  /* ------------------------------------------------------------------ *
   * Dealer turn
   * ------------------------------------------------------------------ */

  /**
   * Play out the dealer's hand from a shoe. Returns the full card list and the
   * order the draws happened in so the caller can animate them one at a time.
   * The dealer never draws when the player has already busted.
   */
  function playDealerTurn(dealerCards, shoe, playerBusted) {
    var cards = (dealerCards || []).slice();
    var draws = [];
    if (playerBusted) return { cards: cards, draws: draws };
    var total = handTotal(cards).total;
    var guard = 0;
    while (dealerShouldHit(total) && guard < 12) {
      var card = shoe.draw();
      cards.push(card);
      draws.push(card);
      total = handTotal(cards).total;
      guard++;
    }
    return { cards: cards, draws: draws };
  }

  return Object.freeze({
    SUITS: SUITS,
    RANKS: RANKS,
    DEFAULT_REWARDS: DEFAULT_REWARDS,
    TIER_BY_RESULT: TIER_BY_RESULT,
    ALLOWED_PERCENTS: ALLOWED_PERCENTS,
    MIN_SUBTOTAL_BY_PERCENT: MIN_SUBTOTAL_BY_PERCENT,
    DEALER_STANDS_ON: DEALER_STANDS_ON,
    CODE_ALPHABET: CODE_ALPHABET,
    CODE_PATTERN: CODE_PATTERN,
    STORAGE_KEY: STORAGE_KEY,
    randomInt: randomInt,
    hasSecureRandom: hasSecureRandom,
    createDeck: createDeck,
    shuffle: shuffle,
    createShoe: createShoe,
    cardValue: cardValue,
    handTotal: handTotal,
    isBlackjack: isBlackjack,
    isBust: isBust,
    dealerShouldHit: dealerShouldHit,
    visibleTotal: visibleTotal,
    resolveOutcome: resolveOutcome,
    tierForResult: tierForResult,
    RESULT_LINES: RESULT_LINES,
    resultLine: resultLine,
    percentForResult: percentForResult,
    percentForTier: percentForTier,
    isValidCode: isValidCode,
    isAllowedPercent: isAllowedPercent,
    buildCode: buildCode,
    accessibleCardLabel: accessibleCardLabel,
    isRedSuit: isRedSuit,
    emptyState: emptyState,
    safeStorage: safeStorage,
    readState: readState,
    writeState: writeState,
    clearState: clearState,
    shouldAutoOpen: shouldAutoOpen,
    canClaim: canClaim,
    canReplay: canReplay,
    claimSession: claimSession,
    replayReward: replayReward,
    playDealerTurn: playDealerTurn
  });
});
